import { Button, Spacer, Typography } from "@fullscript/aviary-web";
import { faChevronRight } from "@fullscript/pro-regular-svg-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { EmailInput } from "@shared/components/EmailInput/EmailInput";
import { isEmailValid } from "@shared/components/EmailInput/EmailValidation/EmailValidation";
import { useGlobalConfig } from "@unauthenticated/shared/context/GlobalConfig/GlobalConfig";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./DirectLabsAuthStartForm.styles";

interface Props {
  initialEmail?: string;
  loading?: boolean;
  onContinue: (email: string) => void;
}

const DirectLabsAuthStartForm = ({ initialEmail = "", loading = false, onContinue }: Props) => {
  const { t } = useTranslation();
  const { emailRegex } = useGlobalConfig();
  const [email, setEmail] = useState(initialEmail);
  const [isEmailDirty, setIsEmailDirty] = useState(false);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setIsEmailDirty(true);
  };

  const handleEmailSet = (emailValue: string) => {
    setEmail(emailValue);
    setIsEmailDirty(true);
  };

  const isValidEmail = isEmailValid(email, emailRegex);

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();

    if (!isValidEmail) {
      setIsEmailDirty(true);

      return;
    }

    onContinue(email);
  };

  const validateEmail = () => {
    if (isEmailDirty && !isValidEmail) {
      return t(l.directLabsCheckout.authStart.errors.emailInvalid);
    }

    return undefined;
  };

  return (
    <div>
      <Typography variant="h2" intention="neutral" colorIntensity="bold">
        {t(l.directLabsCheckout.authStart.title)}
      </Typography>
      <Spacer size="large" />
      <Typography>{t(l.directLabsCheckout.authStart.description)}</Typography>
      <Spacer size="medium" />
      <form css={styles.form} onSubmit={handleSubmit}>
        <EmailInput
          label={t(l.directLabsCheckout.authStart.emailLabel)}
          value={email}
          setEmail={handleEmailSet}
          onChange={handleEmailChange}
          isDirty={isEmailDirty}
          isLoading={loading}
          required
          error={validateEmail()}
          wrapperStyles={styles.emailInput}
        />
        <Button
          type="submit"
          variant="primary"
          isFullWidth
          size="large"
          isPending={loading}
          disabled={!isValidEmail || loading}
          rightIcon={faChevronRight}
        >
          {t(l.directLabsCheckout.authStart.continueButton)}
        </Button>
      </form>
    </div>
  );
};

export { DirectLabsAuthStartForm };
